import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { companyInfo, products, features, industries } from "@/data/mockData";
import {
  ArrowRight,
  Zap,
  Shield,
  Award,
  TrendingUp,
  ZoomIn,
  X,
} from "lucide-react";

const iconMap = { Zap, Shield, Award, TrendingUp };

function useInView(options) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.1, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, inView];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(40px)",
        transitionDelay: `${delay}ms`,
        transitionProperty: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

export default function HomePage() {
  const { isDark } = useTheme();
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    setTimeout(() => setHeroLoaded(true), 100);
  }, []);

  const closeLightbox = useCallback(() => setLightboxImage(null), []);

  return (
    <>
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url("${companyInfo.heroImage}")`,
            }}
          />
          <div className="absolute inset-0 bg-slate-900/80" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-transparent to-slate-900" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-32">
          <div className="max-w-5xl mx-auto text-center">
            <div
              className="inline-block mb-8 tracking-[0.3em] uppercase"
              style={{
                opacity: heroLoaded ? 1 : 0,
                transform: heroLoaded ? "none" : "translateY(20px)",
                transition: "all 0.6s ease 0.1s",
              }}
            >
              <span className="text-amber-400 text-sm md:text-base font-semibold">
                {companyInfo.tagline}
              </span>
            </div>

            <h1
              className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-white mb-8 leading-[1.1]"
              style={{
                opacity: heroLoaded ? 1 : 0,
                transform: heroLoaded ? "none" : "translateY(20px)",
                transition: "all 0.6s ease 0.3s",
              }}
            >
              {companyInfo.name}
            </h1>

            <p
              className="text-lg md:text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed"
              style={{
                opacity: heroLoaded ? 1 : 0,
                transform: heroLoaded ? "none" : "translateY(20px)",
                transition: "all 0.6s ease 0.5s",
              }}
            >
              Powering the world with <span className="font-bold text-white">Mac Wires</span>. Leading manufacturer of Submersible, Bare Copper, and CCR wires in Hyderabad.
            </p>

            <div
              className="flex flex-col sm:flex-row gap-5 justify-center"
              style={{
                opacity: heroLoaded ? 1 : 0,
                transform: heroLoaded ? "none" : "translateY(20px)",
                transition: "all 0.6s ease 0.7s",
              }}
            >
              <button
                onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
                className="group inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all duration-300 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-105 text-lg"
              >
                View Products
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-10 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-amber-500/50 hover:bg-amber-500/10 hover:border-amber-400 transition-all duration-300 text-lg"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce"
          style={{
            opacity: heroLoaded ? 1 : 0,
            transition: "opacity 0.6s ease 1s",
          }}
        >
          <div className="w-6 h-10 border-2 border-amber-400/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-2 bg-amber-400 rounded-full" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={`py-24 ${isDark ? "bg-slate-950" : "bg-gray-50"}`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <AnimatedSection key={feature.title} delay={index * 100} className="h-full">
                  <div
                    data-testid={`feature-card-${index}`}
                    className={`group h-full flex flex-col p-6 rounded-xl border transition-all duration-300 hover:scale-105 ${
                      isDark
                        ? "bg-gradient-to-br from-slate-900 to-slate-800 border-amber-600/20 hover:border-amber-500/50"
                        : "bg-white border-gray-200 hover:border-amber-400 shadow-sm hover:shadow-lg"
                    }`}
                  >
                    <div className="text-amber-500 mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-8 h-8" />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        isDark ? "text-white" : "text-slate-900"
                      }`}
                    >
                      {feature.title}
                    </h3>
                    <p
                      className={`text-sm leading-relaxed flex-1 ${
                        isDark ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {feature.description}
                    </p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section
        id="products"
        data-testid="products-section"
        className={`py-24 ${isDark ? "bg-slate-900" : "bg-white"}`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2
                className={`text-4xl md:text-5xl font-bold mb-4 ${
                  isDark ? "text-white" : "text-slate-900"
                }`}
              >
                Our{" "}
                <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                  Products
                </span>
              </h2>
              <p
                className={`text-lg max-w-2xl mx-auto ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Comprehensive range of manufactured copper wire products for all
                your industrial needs
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <AnimatedSection key={product.id} delay={index * 100} className="h-full">
                <div
                  data-testid={`product-card-${product.id}`}
                  className={`group h-full flex flex-col rounded-xl overflow-hidden border transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl ${
                    isDark
                      ? "bg-slate-950 border-amber-600/20 hover:border-amber-500/50 hover:shadow-amber-500/10"
                      : "bg-white border-gray-200 hover:border-amber-400 hover:shadow-amber-200/30"
                  }`}
                >
                  <div
                    className={`relative h-64 overflow-hidden cursor-pointer ${
                      isDark ? "bg-slate-900" : "bg-gray-100"
                    }`}
                    onClick={() => setLightboxImage({ src: product.image, alt: product.name })}
                    data-testid={`product-image-${product.id}`}
                  >
                    <img
                      alt={product.name}
                      className="w-full h-full object-contain p-2 group-hover:scale-110 transition-transform duration-500"
                      src={product.image}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
                      <div className="bg-amber-500 p-3 rounded-full shadow-lg">
                        <ZoomIn className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    {product.badge && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full shadow-md">
                          {product.badge}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3
                      className={`text-xl font-bold mb-3 group-hover:text-amber-400 transition-colors ${
                        isDark ? "text-white" : "text-slate-900"
                      }`}
                    >
                      {product.name}
                    </h3>
                    <p
                      className={`mb-4 text-sm leading-relaxed ${
                        isDark ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {product.description}
                    </p>
                    <div
                      className={`mb-4 p-3 rounded-lg border ${
                        isDark
                          ? "bg-slate-900/50 border-amber-600/10"
                          : "bg-amber-50 border-amber-200/50"
                      }`}
                    >
                      <p className="text-xs text-amber-500 font-semibold mb-1">
                        Specifications:
                      </p>
                      <p
                        className={`text-sm ${
                          isDark ? "text-gray-300" : "text-slate-700"
                        }`}
                      >
                        {product.specs}
                      </p>
                    </div>
                    <ul className="space-y-2 mt-auto">
                      {product.features.map((feat) => (
                        <li
                          key={feat}
                          className={`flex items-center text-sm ${
                            isDark ? "text-gray-300" : "text-slate-600"
                          }`}
                        >
                          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mr-3 flex-shrink-0" />
                          {feat}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section
        id="industries"
        data-testid="industries-section"
        className={`py-24 ${isDark ? "bg-slate-950" : "bg-gray-50"}`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2
                className={`text-4xl md:text-5xl font-bold mb-4 ${
                  isDark ? "text-white" : "text-slate-900"
                }`}
              >
                Industries We{" "}
                <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                  Serve
                </span>
              </h2>
              <p
                className={`text-lg max-w-2xl mx-auto ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Trusted manufacturing partner for leading industries across
                diverse sectors
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <AnimatedSection key={industry.name} delay={index * 100} className="h-full">
                <div
                  data-testid={`industry-card-${index}`}
                  className={`group h-full flex flex-col relative overflow-hidden rounded-xl border transition-all duration-300 hover:scale-105 ${
                    isDark
                      ? "bg-slate-900 border-amber-600/20 hover:border-amber-500/50"
                      : "bg-white border-gray-200 hover:border-amber-400 shadow-sm hover:shadow-lg"
                  }`}
                >
                  <div className="relative h-48 overflow-hidden flex-shrink-0">
                    <img
                      alt={industry.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      src={industry.image}
                      loading="lazy"
                    />
                    <div
                      className={`absolute inset-0 bg-gradient-to-t ${
                        isDark
                          ? "from-slate-900 via-slate-900/70 to-transparent"
                          : "from-black/60 via-black/30 to-transparent"
                      }`}
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3
                      className={`text-lg font-bold mb-3 group-hover:text-amber-400 transition-colors ${
                        isDark ? "text-white" : "text-slate-900"
                      }`}
                    >
                      {industry.name}
                    </h3>
                    <p
                      className={`text-sm leading-relaxed flex-1 ${
                        isDark ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {industry.description}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className={`py-24 ${
          isDark
            ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
            : "bg-gradient-to-br from-amber-50 via-white to-orange-50"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center">
              <h2
                className={`text-4xl md:text-5xl font-bold mb-6 ${
                  isDark ? "text-white" : "text-slate-900"
                }`}
              >
                Ready to Work With Us?
              </h2>
              <p
                className={`text-xl mb-10 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Get in touch today for premium manufactured copper wire products
                and competitive pricing
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-lg font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all duration-300 shadow-xl shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-105"
              >
                Request a Quote Now
                <ArrowRight className="ml-3 w-6 h-6" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
      {/* Image Lightbox */}
      {lightboxImage && (
        <div
          data-testid="image-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          <button
            data-testid="lightbox-close-btn"
            onClick={closeLightbox}
            className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <X className="w-8 h-8 text-white" />
          </button>
          <div
            className="max-w-4xl max-h-[85vh] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.src}
              alt={lightboxImage.alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg select-none"
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
              style={{ pointerEvents: "auto", userSelect: "none" }}
            />
            <p className="text-center text-white/70 mt-4 text-sm font-medium">
              {lightboxImage.alt}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
